import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { programApi } from '@/domains/system-management/apis/program-management';
import type {
  Params,
  Program,
  UpdateProgramResponse,
} from '@/domains/system-management/types/program';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

export const useprogramManagement = (params: Params) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Get program list
  const usePrograms = () => {
    return useQuery({
      queryKey: ['programs', params],
      queryFn: () => programApi.getPrograms(params),
    });
  };

  // Get program list for select
  const useActivePrograms = () => {
    const staticParams = { deletion: '001002' };
    return useQuery({
      queryKey: ['activeprograms', staticParams],
      queryFn: () => programApi.getPrograms(staticParams),
    });
  };

  // Get program details
  const useProgram = (id: string) => {
    return useQuery({
      queryKey: ['program', id],
      queryFn: () => programApi.getProgram(id),
      enabled: !!id,
    });
  };

  // Create program
  const useCreateProgram = (onCreateSuccess?: (response: { id: string }) => void) => {
    return useMutation({
      mutationFn: (data: Partial<Program>) => programApi.createProgram(data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > registration success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['programs', params] });
            if (onCreateSuccess) onCreateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Create lower program
  const useCreateLowerProgram = (onCreateSuccess?: (response: { id: string }) => void) => {
    return useMutation({
      mutationFn: (data: Partial<Program>) => programApi.createLowerProgram(data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > registration success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['programs', params] });
            if (onCreateSuccess) onCreateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Update program
  const useUpdateProgram = (onUpdateSuccess?: (response: UpdateProgramResponse) => void) => {
    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<Program> }) =>
        programApi.updateProgram(id, data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > update success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['programs', params] });
            queryClient.invalidateQueries({
              queryKey: ['program', responseData.id],
            });
            queryClient.invalidateQueries({ queryKey: ['navbarList'] });
            if (onUpdateSuccess) onUpdateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Update program
  const useUpdateLowerProgram = (onUpdateSuccess?: (response: UpdateProgramResponse) => void) => {
    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<Program> }) =>
        programApi.updateLowerProgram(id, data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > update success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['programs', params] });
            queryClient.invalidateQueries({
              queryKey: ['program', responseData.id],
            });
            queryClient.invalidateQueries({ queryKey: ['navbarList'] });
            if (onUpdateSuccess) onUpdateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Delete program
  const useDeleteProgram = (onDeleteSuccess?: () => void) => {
    return useMutation({
      mutationFn: (id: string) => programApi.deleteProgram(id),
      onSuccess: () => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > delete success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['programs', params] });
            if (onDeleteSuccess) onDeleteSuccess();
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  return {
    useActivePrograms,
    usePrograms,
    useProgram,
    useCreateProgram,
    useCreateLowerProgram,
    useUpdateProgram,
    useUpdateLowerProgram,
    useDeleteProgram,
  };
};
